import React, { useMemo } from "react";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { getDisplayedEntityIds } from "@common/types/domains/accounting/chartOfAccountsViewFilters";
import { ChartOfAccountsColumn } from "@common/types/views/chartOfAccountsView";
import { DEFAULT_CHART_OF_ACCOUNTS_EXPORT_COLUMNS } from "@common/helpers/domains/accounting/chartOfAccountsExportTable";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { ExportMenuItem } from "@design-system/ExportMenuItem/ExportMenuItem";
import {
    ChartOfAccountsExportParams,
    getChartOfAccountsCsv,
    getChartOfAccountsXlsx,
} from "@lib/chartOfAccounts";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Button } from "@design-system/Button/Button";
import { DownloadIcon } from "@icons";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";
import { AccountingReportFilters } from "../types";
import { useCoaFiltersActive } from "./useCoaFiltersActive";

enum ChartOfAccountsExportFormat {
    CSV = "csv",
    XLSX = "xlsx",
}

function buildFilteredExportParams({
    filters,
    currentColumns,
    allEntityIds,
    availableClassifications,
}: {
    filters: AccountingReportFilters;
    currentColumns: ChartOfAccountsColumn[];
    allEntityIds: number[];
    availableClassifications?: AccountClass[];
}): ChartOfAccountsExportParams {
    const displayedEntityIds = getDisplayedEntityIds(filters);
    const entityIds =
        displayedEntityIds && displayedEntityIds.length > 0
            ? displayedEntityIds
            : allEntityIds;

    return {
        entityIds,
        availableClassifications,
        accountStatus: filters.accountStatus,
        accountSource: filters.accountSource,
        accountRolePresence: filters.accountRolePresence,
        accountRulePresence: filters.accountRulePresence,
        accountSubtypes: filters.accountSubtypes,
        form1099Boxes: filters.form1099Boxes,
        search: filters.search,
        coaTemplateGroupKeys: filters.coaTemplateGroupKeys,
        columns: currentColumns,
    };
}

function buildAllExportParams({
    defaultColumns,
    allEntityIds,
    availableClassifications,
}: {
    defaultColumns: ChartOfAccountsColumn[];
    allEntityIds: number[];
    availableClassifications?: AccountClass[];
}): ChartOfAccountsExportParams {
    return {
        entityIds: allEntityIds,
        availableClassifications,
        columns: defaultColumns,
    };
}

function useChartOfAccountsExportDownload({
    format,
    params,
}: {
    format: ChartOfAccountsExportFormat;
    params: ChartOfAccountsExportParams;
}) {
    return useFileDownload(async () => {
        if (format === ChartOfAccountsExportFormat.CSV) {
            const response = await getChartOfAccountsCsv(params);
            saveBlobResponse(response, "Chart of Accounts.csv");
            return;
        }

        const response = await getChartOfAccountsXlsx(params);
        saveBlobResponse(response, "Chart of Accounts.xlsx");
    });
}

export const ChartOfAccountsExport: React.FC<{
    filters: AccountingReportFilters;
    columns: ChartOfAccountsColumn[];
    availableClassifications?: AccountClass[];
}> = ({ filters, columns, availableClassifications }) => {
    const entities = useEntitiesWithAccountingAvailable();

    const allEntityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );

    const isFiltersActive = useCoaFiltersActive({ filters, allEntityIds });

    const defaultColumns = useMemo(
        () =>
            DEFAULT_CHART_OF_ACCOUNTS_EXPORT_COLUMNS.filter(
                (column) =>
                    column !== ChartOfAccountsColumn.TYPE &&
                    column !== ChartOfAccountsColumn.SUBTYPE,
            ),
        [],
    );

    const filteredExportParams = useMemo(
        () =>
            buildFilteredExportParams({
                filters,
                currentColumns: columns,
                allEntityIds,
                availableClassifications,
            }),
        [filters, columns, allEntityIds, availableClassifications],
    );

    const allExportParams = useMemo(
        () =>
            buildAllExportParams({
                defaultColumns,
                allEntityIds,
                availableClassifications,
            }),
        [defaultColumns, allEntityIds, availableClassifications],
    );

    const downloadFilteredCsv = useChartOfAccountsExportDownload({
        format: ChartOfAccountsExportFormat.CSV,
        params: filteredExportParams,
    });
    const downloadFilteredXlsx = useChartOfAccountsExportDownload({
        format: ChartOfAccountsExportFormat.XLSX,
        params: filteredExportParams,
    });
    const downloadAllCsv = useChartOfAccountsExportDownload({
        format: ChartOfAccountsExportFormat.CSV,
        params: allExportParams,
    });
    const downloadAllXlsx = useChartOfAccountsExportDownload({
        format: ChartOfAccountsExportFormat.XLSX,
        params: allExportParams,
    });

    return (
        <Menu.Root
            trigger={
                <Button
                    variant="primary"
                    iconOnly
                    size="md"
                    aria-label="Export chart of accounts"
                >
                    <DownloadIcon />
                </Button>
            }
        >
            {isFiltersActive ? (
                <>
                    <ExportMenuItem
                        isDownloading={downloadFilteredCsv.isDownloading}
                        onClick={() => downloadFilteredCsv.download()}
                    >
                        Filtered chart of accounts (.csv)
                    </ExportMenuItem>
                    <ExportMenuItem
                        isDownloading={downloadFilteredXlsx.isDownloading}
                        onClick={() => downloadFilteredXlsx.download()}
                    >
                        Filtered chart of accounts (.xlsx)
                    </ExportMenuItem>
                </>
            ) : null}

            <ExportMenuItem
                isDownloading={downloadAllCsv.isDownloading}
                onClick={() => downloadAllCsv.download()}
            >
                All chart of accounts (.csv)
            </ExportMenuItem>
            <ExportMenuItem
                isDownloading={downloadAllXlsx.isDownloading}
                onClick={() => downloadAllXlsx.download()}
            >
                All chart of accounts (.xlsx)
            </ExportMenuItem>
        </Menu.Root>
    );
};
